
import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";

export const NotificationsCard = () => {
  const [notificationPreferences, setNotificationPreferences] = useState({
    emailNotifications: false,
    achievementNotifications: false,
    suggestionNotifications: false,
    reminderNotifications: false
  });
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const loadUserData = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) {
          navigate('/login');
          return;
        }

        const { data: preferences, error: preferencesError } = await supabase
          .from('user_email_preferences')
          .select(`
            notifications_enabled,
            achievement_notifications,
            suggestion_notifications,
            reminder_notifications
          `)
          .eq('user_id', session.user.id)
          .maybeSingle();

        if (preferencesError && preferencesError.code !== 'PGRST116') {
          throw preferencesError;
        }

        if (preferences) {
          setNotificationPreferences({
            emailNotifications: preferences.notifications_enabled,
            achievementNotifications: preferences.achievement_notifications,
            suggestionNotifications: preferences.suggestion_notifications,
            reminderNotifications: preferences.reminder_notifications
          });
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        toast({
          title: "Error",
          description: "Failed to load user data",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadUserData();
  }, [navigate, toast]);

  const handleSave = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/login');
        return;
      }

      const { error } = await supabase
        .from('user_email_preferences')
        .upsert({
          user_id: session.user.id,
          notifications_enabled: notificationPreferences.emailNotifications,
          achievement_notifications: notificationPreferences.achievementNotifications,
          suggestion_notifications: notificationPreferences.suggestionNotifications,
          reminder_notifications: notificationPreferences.reminderNotifications
        }, { 
          onConflict: 'user_id',
          ignoreDuplicates: false 
        });

      if (error) throw error;
      
      toast({
        title: "Settings Updated",
        description: "Your notification preferences have been saved.",
      });
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast({
        title: "Error",
        description: "Failed to save preferences",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="mb-4">Notification Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-6">
          <div className="flex items-center justify-between space-x-4">
            <div>
              <Label htmlFor="email-notifications" className="text-base font-medium">
                Email Notifications
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Receive important updates and notifications via email
              </p>
            </div>
            <Switch
              id="email-notifications"
              checked={notificationPreferences.emailNotifications}
              onCheckedChange={(checked) =>
                setNotificationPreferences((prev) => ({
                  ...prev,
                  emailNotifications: checked,
                }))
              }
            />
          </div>

          <div className="flex items-center justify-between space-x-4">
            <div>
              <Label htmlFor="achievement-notifications" className="text-base font-medium">
                Achievement Notifications
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Get notified about milestones and achievements
              </p>
            </div>
            <Switch
              id="achievement-notifications"
              checked={notificationPreferences.achievementNotifications}
              onCheckedChange={(checked) =>
                setNotificationPreferences((prev) => ({
                  ...prev,
                  achievementNotifications: checked,
                }))
              }
            />
          </div>

          <div className="flex items-center justify-between space-x-4">
            <div>
              <Label htmlFor="suggestion-notifications" className="text-base font-medium">
                Learning Suggestions
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Receive personalized learning recommendations
              </p>
            </div>
            <Switch
              id="suggestion-notifications"
              checked={notificationPreferences.suggestionNotifications}
              onCheckedChange={(checked) =>
                setNotificationPreferences((prev) => ({
                  ...prev,
                  suggestionNotifications: checked,
                }))
              }
            />
          </div>

          <div className="flex items-center justify-between space-x-4">
            <div>
              <Label htmlFor="reminder-notifications" className="text-base font-medium">
                Activity Reminders
              </Label>
              <p className="text-sm text-muted-foreground mt-1">
                Get reminded about upcoming or pending activities
              </p>
            </div>
            <Switch
              id="reminder-notifications"
              checked={notificationPreferences.reminderNotifications}
              onCheckedChange={(checked) =>
                setNotificationPreferences((prev) => ({
                  ...prev,
                  reminderNotifications: checked,
                }))
              }
            />
          </div>
        </div>

        <Button onClick={handleSave} className="w-full">
          Save Notification Settings
        </Button>
      </CardContent>
    </Card>
  );
};
